import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import useIsMountedRef from "src/hooks/useIsMountedRef";
import { useLicenseValidation } from "src/hooks/useLicenseValidation";
import { AiAgentDto, KnowledgeSyncStatus } from "src/types/aiAgent";
import { Loader } from "../shared";
import { Helmet } from "react-helmet-async";
import { Formik } from "formik";
import * as Yup from "yup";
import { SelectListItem } from "src/types/shared";
import { LicenseType, UserLimitValidationResult } from "src/types/user";
import { Template } from "src/types/templates";
import { templateService } from "src/services/templates";
import {
	HTTPSTATUSCODES,
	SkillsFieldLimits,
	SkillsTranslationKeys,
	GeneratedMediaFileLanguage,
} from "src/utils/constants";
import ChangesFormActions from "../shared/formChanges/ChangesFormActions";
import {
	Grid,
	Card,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	FormHelperText,
	Tooltip,
	Stack,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { CustomTextField } from "../shared/CustomTextField";
import { MenuRoutes } from "src/types/routes";
import { setRowIdForUserSkills } from "src/utils/skillHelper";
import { setRowIdForKnowledgeDocuments } from "src/utils/knowledgeDocumentHelper";
import { skillService } from "src/services/skills";
import { aiAgentService } from "src/services/aiAgents";
import { knowledgeDocumentService } from "src/services/knowledgeDocuments";
import { aiFeaturesConfigurationService } from "src/services/aiFeaturesConfiguration";
import {
	AIAssistant,
	FoundryModelCapability,
} from "src/types/aiFeaturesConfiguration";
import { buildAgentUsageTooltip } from "src/utils/aiAgentUsageHelper";
import SkillsSection from "../skills/SkillsSection";
import KnowledgeDocumentsSection from "./KnowledgeDocumentsSection";
import McpServersSection from "./McpServersSection/McpServersSection";
import { AiAgentSkill } from "src/types/skill";
import { KnowledgeDocumentListItem, AiAgentKnowledgeDocumentDto } from "src/types/knowledgeDocument";
import { McpServerDto } from "src/types/mcpServer";
import { mcpServerService } from "src/services/mcpServers";
import { announcementService } from "src/services/announcements";
import { Language, Voice } from "src/types/announcement";
import Lockr from "lockr";
import { StorageKeys } from "src/utils/storageKeys";
import find from "lodash/find";

const REQUIRED_FOUNDRY_MODELS = ["gpt-realtime-mini", "gpt-realtime-2.1-mini"];

const EditAiAgent: FC = () => {
	const { t } = useTranslation();
	const isMountedRef = useIsMountedRef();
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const { displayLicenseValidationMessages } = useLicenseValidation();

	const { agentId } = useParams();
	const [agent, setAgent] = useState<AiAgentDto | null>({} as AiAgentDto);

	const [assignableSkills, setAssignableSkills] = useState<SelectListItem[]>(
		[] as SelectListItem[]
	);

	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [foundryAgents, setFoundryAgents] = useState<AIAssistant[]>([]);
	const [foundryModels, setFoundryModels] = useState<any[]>([]);
	const [systemPromptTemplates, setSystemPromptTemplates] = useState<
		Template[]
	>([]);
	const [isLoadingAgents, setIsLoadingAgents] = useState<boolean>(true);
	const [isLoadingModels, setIsLoadingModels] = useState<boolean>(true);
	const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(true);
	const [languages, setLanguages] = useState<Language[]>([]);
	const [voices, setVoices] = useState<Voice[]>([]);
	const [filteredVoices, setFilteredVoices] = useState<Voice[]>([]);
	const [isLoadingVoices, setIsLoadingVoices] = useState<boolean>(true);
	const [selectedLanguageId, setSelectedLanguageId] = useState<string>("");
	const [availableKnowledgeDocuments, setAvailableKnowledgeDocuments] = useState<KnowledgeDocumentListItem[]>([]);
	const [isLoadingKnowledgeDocuments, setIsLoadingKnowledgeDocuments] = useState<boolean>(true);
	const [allMcpServers, setAllMcpServers] = useState<McpServerDto[]>([]);

	const route = MenuRoutes.AiAgents;

	const displayedFoundryModels = [
		...foundryModels,
		...REQUIRED_FOUNDRY_MODELS.filter(
			(model) => !foundryModels.includes(model)
		),
	];

	useEffect(() => {
		aiFeaturesConfigurationService.getAgents()
			.then((result) => {
				setFoundryAgents(result?.data || []);
			})
			.catch((error) => {
				console.error("Error loading foundry agents:", error);
			})
			.finally(() => {
				setIsLoadingAgents(false);
			});
	}, []);

	useEffect(() => {
		aiFeaturesConfigurationService.getModels(FoundryModelCapability.Realtime)
			.then((result) => {
				setFoundryModels(result?.data || []);
			})
			.catch((error) => {
				console.error("Error loading foundry models:", error);
			})
			.finally(() => {
				setIsLoadingModels(false);
			});
	}, []);

	const getLanguagesAndVoices = async () => {
		try {
			const tenantId = Lockr.get(StorageKeys.TenantId) || "";
			const response = await announcementService.getLanguagesAndVoices(
				tenantId
			);
			if (response) {
				setLanguages(response.data.languages);
				setVoices(response.data.voices);
			}
		} catch (err) {
			console.error(err);
			enqueueSnackbar(t("Announcement.FetchingLanguagesAndVoicesFailed"), {
				variant: "error",
			});
		} finally {
			setIsLoadingVoices(false);
		}
	};

	useEffect(() => {
		getLanguagesAndVoices();
	}, []);

	useEffect(() => {
		if (voices.length === 0) {
			return;
		}
		if (agentId && agent?.azureVoiceName) {
			const voice = find(voices, ["id", agent.azureVoiceName]);
			setSelectedLanguageId(voice ? voice.languageId : GeneratedMediaFileLanguage.Default);
		} else {
			setSelectedLanguageId(GeneratedMediaFileLanguage.Default);
		}
	}, [voices, agent]);

	useEffect(() => {
		if (selectedLanguageId) {
			const localFilteredVoices = voices.filter(
				(item) => item.languageId === selectedLanguageId
			);
			setFilteredVoices(localFilteredVoices);
		}
	}, [selectedLanguageId, voices]);

	const getSystemPromptTemplates = async () => {
		try {
			const response =
				await templateService.getAiAgentSystemPromptTemplates();
			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setSystemPromptTemplates(response.data || []);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoadingTemplates(false);
		}
	};

	useEffect(() => {
		getSystemPromptTemplates();
	}, []);

	const getAiAgent = async () => {
		if (agentId) {
			try {
				const response = await aiAgentService.getAiAgentById(agentId);

				if (
					isMountedRef.current &&
					response?.status == HTTPSTATUSCODES.StatusCodeSuccess
				) {
					const data = response.data;

					if (data.skills && data.skills.length > 0) {
						setRowIdForUserSkills(data.skills);
					}

					if (data.knowledgeDocuments && data.knowledgeDocuments.length > 0) {
						setRowIdForKnowledgeDocuments(data.knowledgeDocuments);
					}

					setAgent(data);
					setIsDataLoaded(true);
				}
			} catch (error) {
				console.error(error);
			}
		} else {
			setIsDataLoaded(true);
		}
	};

	useEffect(() => {
		getAiAgent();
	}, [agentId]);

	const handleResetForm = (handleReset: (e: any) => void) => {
		handleReset(null);
	};

	const handleNavigateBack = () => {
		navigate(route);
	};

	const handleCancelForm = (
		handleReset: (e: any) => void,
		dirty: boolean
	) => {
		if (dirty) {
			handleResetForm(handleReset);
		} else {
			handleNavigateBack();
		}
	};

	const getAllSkills = async (): Promise<void> => {
		try {
			const response = await skillService.getAllSkills();

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setAssignableSkills(response.data);
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		getAllSkills();
	}, []);

	const getKnowledgeDocuments = async () => {
		try {
			const response = await knowledgeDocumentService.getAllKnowledgeDocuments();
			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setAvailableKnowledgeDocuments(response.data || []);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoadingKnowledgeDocuments(false);
		}
	};

	useEffect(() => {
		getKnowledgeDocuments();
	}, []);

	useEffect(() => {
		mcpServerService.getMcpServers("").then((res) => {
			setAllMcpServers(res.data?.items ?? []);
		}).catch(err => console.error("Failed to load MCP servers:", err));
	}, []);

	const handleRetryKnowledgeSync = async () => {
		if (!agentId) {
			return;
		}
		try {
			await aiAgentService.retryKnowledgeSync(agentId);
			setAgent((prev) => prev ? { ...prev, knowledgeSyncStatus: KnowledgeSyncStatus.InProgress } : prev);
		} catch (error) {
			console.error("Failed to retry knowledge sync:", error);
		}
	};

	const getAssignedSkills = (skills: AiAgentSkill[]) => {
		const assignedSkills = skills.filter((x) => Boolean(x.skillId));

		return assignedSkills.length > 0
			? assignedSkills.map((x) => x.skillId)
			: [];
	};

	const FIELD_KNOWLEDGE_DOCUMENTS = "knowledgeDocuments";
	const FIELD_KNOWLEDGE_DOCUMENT_NAME = "knowledgeDocumentName";
	const FIELD_KNOWLEDGE_DOCUMENT_DESCRIPTION = "description";

	const handleKnowledgeDocumentSelect = (
		index: number,
		documentId: string,
		setFieldValue: (field: string, value: any) => void
	) => {
		const selectedDoc = availableKnowledgeDocuments.find((d) => d.id === documentId);
		if (selectedDoc) {
			setFieldValue(`${FIELD_KNOWLEDGE_DOCUMENTS}.${index}.${FIELD_KNOWLEDGE_DOCUMENT_NAME}`, selectedDoc.name);
			setFieldValue(`${FIELD_KNOWLEDGE_DOCUMENTS}.${index}.${FIELD_KNOWLEDGE_DOCUMENT_DESCRIPTION}`, selectedDoc.description);
		} else {
			setFieldValue(`${FIELD_KNOWLEDGE_DOCUMENTS}.${index}.${FIELD_KNOWLEDGE_DOCUMENT_NAME}`, "");
			setFieldValue(`${FIELD_KNOWLEDGE_DOCUMENTS}.${index}.${FIELD_KNOWLEDGE_DOCUMENT_DESCRIPTION}`, null);
		}
	};

	const handleSaveError = (error: any) => {
		const status = error?.response?.status;

		if (status === HTTPSTATUSCODES.StatusCodeForbidden) {
			enqueueSnackbar(t("AiAgentList.EntityDoesNotExist"), {
				variant: "error",
			});
		} else if (status === HTTPSTATUSCODES.StatusCodeConflict) {
			const data = error.response?.data;
			if (
				Array.isArray(data) &&
				data.length > 0 &&
				"limitedUserType" in data[0]
			) {
				const validationResults = data as UserLimitValidationResult[];
				displayLicenseValidationMessages(validationResults);
			} else {
				enqueueSnackbar(t("AiAgentList.AgentNameExists"), {
					variant: "error",
				});
			}
		} else {
			enqueueSnackbar(
				t(
					agentId
						? "AiAgentList.UpdateFailed"
						: "AiAgentList.CreateFailed"
				),
				{ variant: "error" }
			);
		}

		console.error(error);
	};

	const saveAiAgent = async (agentData: AiAgentDto): Promise<void> => {
		setIsSubmitting(true);

		try {
			const response = agentId
				? await aiAgentService.updateAiAgent(agentData)
				: await aiAgentService.createAiAgent(agentData);

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {

				enqueueSnackbar(
					t(
						agentId
							? "AiAgentList.UpdateSucceeded"
							: "AiAgentList.CreateSucceeded"
					),
					{ variant: "success" }
				);

				setIsSubmitting(false);
				navigate(route);
			}
		} catch (error) {
			handleSaveError(error);
			setIsSubmitting(false);
		}
	};

	const saveAgentByValues = (values: any) => {
		const validKnowledgeDocuments = (values.knowledgeDocuments || [])
			.filter((d: AiAgentKnowledgeDocumentDto) => Boolean(d.knowledgeDocumentId));

		const validMcpServers = (values.mcpServers || [])
			.filter((s: any) => Boolean(s.mcpServerId))
			.map((s: any) => {
				// Remove temporary IDs (like "0.123456789") — only keep real Guids
				const isValidGuid = s.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.id);
				return {
					mcpServerId: s.mcpServerId,
					...(isValidGuid && { id: s.id }),
				};
			});

		const agentData: AiAgentDto = agentId
			? {
					id: agentId,
					name: values.name,
					microsoftFoundryAgentId: values.agentId,
					currentUsage: agent?.currentUsage || 0,
					maxUsage: values.maxUsage,
					maxWorkload: values.maxWorkload,
					userSkills: values.aiAgentsSkills,
					licenseType: values.licenseType,
					azureVoiceName: values.azureVoiceName,
					microsoftFoundryModelName: values.modelName,
					instructionsTemplateId: values.templateId || undefined,
					knowledgeDocuments: validKnowledgeDocuments,
					maxTokenBudget: values.maxTokenBudget,
					mcpServers: validMcpServers,
			  }
			: ({
					name: values.name,
					microsoftFoundryAgentId: values.agentId,
					currentUsage: 0,
					maxUsage: values.maxUsage,
					maxWorkload: values.maxWorkload,
					userSkills: values.aiAgentsSkills,
					licenseType: values.licenseType,
					azureVoiceName: values.azureVoiceName,
					microsoftFoundryModelName: values.modelName,
					instructionsTemplateId: values.templateId || undefined,
					knowledgeDocuments: validKnowledgeDocuments,
					maxTokenBudget: values.maxTokenBudget,
					mcpServers: validMcpServers,
			  } as AiAgentDto);

		saveAiAgent(agentData);
	};

	return (
		<>
			<Loader isOpen={!isDataLoaded} isInline={false} />
			<Helmet>
				<title>
					{t(
						agentId
							? "AiAgentList.EditAiAgent"
							: "AiAgentList.CreateAiAgent"
					)}{" "}
					| ContactCenter4All
				</title>
			</Helmet>
			{isDataLoaded && (
				<Card sx={{ pl: 3, pb: 4, pr: 3, pt: 4 }}>
					<Formik
						initialValues={{
							name: agentId && agent?.name ? agent.name : "",
							agentId:
								agentId && agent?.microsoftFoundryAgentId
									? agent.microsoftFoundryAgentId
									: "",
							maxUsage:
								agentId && agent?.maxUsage ? agent.maxUsage : 1,
							maxWorkload:
								agentId && agent?.maxWorkload
									? agent.maxWorkload
									: 100,
							aiAgentsSkills:
								agentId && agent.userSkills
									? agent.userSkills
									: ([] as AiAgentSkill[]),
							licenseType:
								agentId && agent?.licenseType
									? agent.licenseType
									: LicenseType.AiOmni,
							azureVoiceName:
								agentId && agent?.azureVoiceName
									? agent.azureVoiceName
									: "",
							modelName:
								agentId && agent?.microsoftFoundryModelName
									? agent.microsoftFoundryModelName
									: "",
							templateId:
								agentId && agent?.instructionsTemplateId
									? agent.instructionsTemplateId
									: "",
							knowledgeDocuments:
								agentId && agent?.knowledgeDocuments
									? agent.knowledgeDocuments
									: ([] as AiAgentKnowledgeDocumentDto[]),
							maxTokenBudget:
								agentId && agent?.maxTokenBudget
									? agent.maxTokenBudget
									: 10000,
							mcpServers:
								agentId && agent?.mcpServers
									? agent.mcpServers.map(s => ({ id: s.id || crypto.randomUUID(), mcpServerId: s.mcpServerId }))
									: ([] as any[]),
						}}
						validationSchema={(() => {
							const invalidValueMsg = t("AiAgentList.InvalidValue");
							return Yup.object().shape({
							name: Yup.string()
								.required(t("AiAgentList.NameRequired"))
								.max(255, t("AiAgentList.NameTooLong")),
							agentId: Yup.string()
								.required(t("AiAgentList.AgentIdRequired"))
								.max(255, t("AiAgentList.AgentIdTooLong")),
							maxUsage: Yup.number()
								.integer(invalidValueMsg)
								.min(1, t("AiAgentList.MaxUsageMinError"))
								.required(t("AiAgentList.MaxUsageRequired")),
							maxWorkload: Yup.number()
								.integer(invalidValueMsg)
								.min(100, t("AiAgentList.MaxWorkloadMinError"))
								.required(t("AiAgentList.MaxWorkloadRequired")),
							azureVoiceName: Yup.string().max(
								255,
								t("AiAgentList.AzureVoiceNameTooLong")
							),
							modelName: Yup.string(),
							templateId: Yup.string(),
							maxTokenBudget: Yup.number()
								.integer(invalidValueMsg)
								.min(1000, t("AiAgentList.KnowledgeDocuments.MinTokenBudgetError"))
								.max(100000, t("AiAgentList.KnowledgeDocuments.MaxTokenBudgetError"))
								.required(t("AiAgentList.KnowledgeDocuments.TokenBudgetRequired")),
							aiAgentsSkills: Yup.array().of(
								Yup.object().shape({
									skillId: Yup.string().required(
										t(SkillsTranslationKeys.Required)
									),
									skillLevel: Yup.number()
										.integer(t("Skills.InvalidValue"))
										.max(
											SkillsFieldLimits.SkillLevelMaxValue,
											`${t(
												"Skills.SkillLevelMaxValueError"
											)} ${
												SkillsFieldLimits.SkillLevelMaxValue
											}`
										)
										.min(
											SkillsFieldLimits.SkillLevelMinValue,
											`${t(
												"Skills.SkillLevelMinValueError"
											)} ${
												SkillsFieldLimits.SkillLevelMinValue
											}`
										)
										.required(
											t(SkillsTranslationKeys.Required)
										),
								})
							),
						});
						})()}
						onSubmit={(values) => {
							saveAgentByValues(values);
						}}
					>
						{({
							errors,
							handleBlur,
							handleChange,
							handleSubmit,
							handleReset,
							touched,
							values,
							dirty,
							setFieldValue,
						}): JSX.Element => {
							return (
							<form onSubmit={handleSubmit}>
								<ChangesFormActions
									isValid={dirty}
									isSubmitting={isSubmitting}
									handleReset={() =>
										handleCancelForm(handleReset, dirty)
									}
									isSaveAction={false}
								/>
								<Grid container spacing={2}>
									<Grid item md={5} xs={11}>
										<CustomTextField
											size="small"
											color="info"
											fullWidth
											label={t("AiAgentList.Name")}
											name="name"
											value={values.name}
											onChange={handleChange}
											onBlur={handleBlur}
											variant="outlined"
											data-testid="name"
											error={Boolean(
												touched.name && errors.name
											)}
											helperText={
												touched.name && errors.name
											}
										/>
									</Grid>
									<Grid item md={5} xs={11}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<FormControl
												fullWidth
												size="small"
												error={Boolean(
													touched.agentId &&
														errors.agentId
												)}
												disabled={isLoadingAgents}
											>
												<InputLabel id="foundry-agent-select-label">
													{t(
														"AiAgentList.MicrosoftFoundryAgent"
													)}
												</InputLabel>
												<Select
													labelId="foundry-agent-select-label"
													id="foundry-agent-select"
													name="agentId"
													value={values.agentId}
													label={t(
														"AiAgentList.MicrosoftFoundryAgent"
													)}
													onChange={handleChange}
													onBlur={handleBlur}
													data-testid="agentId"
												>
													<MenuItem value="">
														{t("AiAgentList.SelectAgent")}
													</MenuItem>
													{foundryAgents?.map(
														(foundryAgent) => {
															const isUsed = foundryAgent.id !== values.agentId &&
																((foundryAgent.usedByFeatures?.length ?? 0) > 0 || (foundryAgent.usedByAgents?.length ?? 0) > 0);
															const menuItem = (
																<MenuItem
																	key={foundryAgent.id}
																	value={foundryAgent.id}
																	disabled={isUsed}
																	sx={isUsed ? {
																		"&.Mui-disabled": {
																			opacity: 0.6,
																			pointerEvents: "auto",
																			cursor: "not-allowed",
																			"&:hover": {
																				backgroundColor: "action.hover",
																			},
																		},
																	} : undefined}
																>
																	{foundryAgent.name}
																</MenuItem>
															);

															return isUsed ? (
																<Tooltip
																	key={foundryAgent.id}
																	title={buildAgentUsageTooltip(t, foundryAgent.usedByFeatures, foundryAgent.usedByAgents)}
																	placement="right"
																>
																	<span>{menuItem}</span>
																</Tooltip>
															) : menuItem;
														}
													)}
												</Select>
												<FormHelperText>
													{touched.agentId && errors.agentId}
												</FormHelperText>
											</FormControl>
											<Tooltip title={t("AiAgentList.MessagingOnly")}>
												<Info
													color="primary"
													fontSize="small"
												/>
											</Tooltip>
										</Stack>
									</Grid>
									<Grid item md={5} xs={11}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<FormControl
												fullWidth
												size="small"
												error={Boolean(
													touched.templateId &&
														errors.templateId
												)}
												disabled={isLoadingTemplates}
											>
												<InputLabel id="system-prompt-template-select-label">
													{t(
														"AiAgentList.SystemPromptTemplate"
													)}
												</InputLabel>
												<Select
													labelId="system-prompt-template-select-label"
													id="system-prompt-template-select"
													name="templateId"
													value={values.templateId}
													label={t(
														"AiAgentList.SystemPromptTemplate"
													)}
													onChange={handleChange}
													onBlur={handleBlur}
													data-testid="templateId"
												>
													<MenuItem value="">
														{t("AiAgentList.SelectTemplate")}
													</MenuItem>
													{systemPromptTemplates?.map(
														(template) => (
															<MenuItem
																key={
																	template.id
																}
																value={
																	template.id
																}
															>
																{template.name}
															</MenuItem>
														)
													)}
												</Select>
												<FormHelperText>
													{touched.templateId && errors.templateId}
												</FormHelperText>
											</FormControl>
										</Stack>
									</Grid>
									<Grid item md={5} xs={11}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<FormControl
												fullWidth
												size="small"
												error={Boolean(
													touched.modelName &&
													errors.modelName
												)}
												disabled={isLoadingModels}
											>
												<InputLabel id="microsoft-foundry-model-select-label">
													{t(
														"AiAgentList.MicrosoftFoundryModel"
													)}
												</InputLabel>
												<Select
													labelId="microsoft-foundry-model-select-label"
													id="microsoft-foundry-model-select"
													name="modelName"
													value={values.modelName}
													label={t(
														"AiAgentList.MicrosoftFoundryModel"
													)}
													onChange={handleChange}
													onBlur={handleBlur}
													data-testid="modelName"
												>
													<MenuItem value="">
														{t("AiAgentList.SelectModel")}
													</MenuItem>
													{displayedFoundryModels.map((model) => (
														<MenuItem
															key={model}
															value={model}
														>
															{model}
														</MenuItem>
													))}
												</Select>
												<FormHelperText>
													{touched.modelName && errors.modelName}
												</FormHelperText>
											</FormControl>
											<Tooltip title={t("AiAgentList.VoiceOnly")}>
												<Info
													color="primary"
													fontSize="small"
												/>
											</Tooltip>
										</Stack>
									</Grid>
									<Grid item md={5} xs={11}>
										<FormControl
											fullWidth
											size="small"
											disabled={isLoadingVoices}
										>
											<InputLabel id="language-select-label">
												{t("Announcement.Language")}
											</InputLabel>
											<Select
												labelId="language-select-label"
												id="language-select"
												value={selectedLanguageId}
												label={t("Announcement.Language")}
												onChange={(event) => {
													setSelectedLanguageId(
														event.target.value
													);
													handleChange({
														target: {
															name: "azureVoiceName",
															value: "",
														},
													});
												}}
												data-testid="languageId"
											>
												{languages.map((language) => (
													<MenuItem
														key={language.id}
														value={language.id}
													>
														{language.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
									<Grid item md={5} xs={11}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<FormControl
												fullWidth
												size="small"
												error={Boolean(
													touched.azureVoiceName &&
														errors.azureVoiceName
												)}
												disabled={isLoadingVoices}
											>
												<InputLabel id="voice-select-label">
													{t("Announcement.Voice")}
												</InputLabel>
												<Select
													labelId="voice-select-label"
													id="voice-select"
													name="azureVoiceName"
													value={values.azureVoiceName}
													label={t("Announcement.Voice")}
													onChange={handleChange}
													onBlur={handleBlur}
													data-testid="azureVoiceName"
												>
													<MenuItem value="">
														{t("AiAgentList.SelectVoice")}
													</MenuItem>
													{filteredVoices.map((voice) => (
														<MenuItem
															key={voice.id}
															value={voice.id}
														>
															{voice.name}
														</MenuItem>
													))}
												</Select>
												<FormHelperText>
													{touched.azureVoiceName &&
														errors.azureVoiceName}
												</FormHelperText>
											</FormControl>
											<Tooltip title={t("AiAgentList.VoiceOnly")}>
												<Info
													color="primary"
													fontSize="small"
												/>
											</Tooltip>
										</Stack>
									</Grid>
									<Grid item md={5} xs={11}>
										<CustomTextField
											size="small"
											color="info"
											fullWidth
											label={t("AiAgentList.MaxWorkload")}
											name="maxWorkload"
											type="number"
											value={values.maxWorkload}
											onChange={handleChange}
											onBlur={handleBlur}
											variant="outlined"
											data-testid="maxWorkload"
											error={Boolean(
												touched.maxWorkload &&
													errors.maxWorkload
											)}
											helperText={
												touched.maxWorkload &&
												errors.maxWorkload
											}
										/>
									</Grid>
									<Grid item xs={12} />
									<SkillsSection
										values={values}
										handleChange={handleChange}
										handleBlur={handleBlur}
										assignableOptions={assignableSkills}
										errors={errors}
										touched={touched}
										assignedOptions={getAssignedSkills(
											values.aiAgentsSkills
										)}
										userId={agentId}
										optionsTitle={t("AiAgentList.Skill")}
										optionName="skillId"
										fieldName="aiAgentsSkills"
										skillsArray={values.aiAgentsSkills}
									/>
									<Grid item xs={12} />
									<KnowledgeDocumentsSection
										availableDocuments={availableKnowledgeDocuments}
										isLoadingDocuments={isLoadingKnowledgeDocuments}
										onDocumentSelect={(index, documentId) =>
											handleKnowledgeDocumentSelect(index, documentId, setFieldValue)
										}
										syncStatus={agent?.knowledgeSyncStatus}
										onRetrySync={handleRetryKnowledgeSync}
									/>
									<McpServersSection
										allServers={allMcpServers}
										syncStatus={agent?.mcpServerSyncStatus}
									/>
								</Grid>
							</form>
						);}}
					</Formik>
				</Card>
			)}
		</>
	);
};

export default EditAiAgent;
